import { useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Typography } from '@healingpaper/cell'

import imageViewer from '@/common/utils/imageViewer'
import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import SquareImage from '@/components/atomic/molecules/SquareImage'
import { getResizedImageUrl } from '@/utils/image-resizer/imageResizer'

export interface PreviewPhoto {
  url: string | null
  title?: string
  alt?: string
  blurred?: boolean
}

export interface Props {
  className?: string
  photos: PreviewPhoto[]
  'aria-label'?: string
  width?: string
  height?: string
  useViewer?: boolean
  isLocked?: boolean
  onClickPhoto?: (index: number) => void
}

const PreviewPhotoSlider = ({
  className,
  photos,
  'aria-label': ariaLabel,
  width = '130px',
  height = '130px',
  useViewer = false,
  isLocked = false,
  onClickPhoto,
}: Props): JSX.Element => {
  const photosRef = useRef<HTMLDivElement>(null)

  const [isPhotosDragging, setIsPhotosDragging] = useState(false)

  function onMouseMovePhotos(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const photosRefCurrent = photosRef.current

    if (isPhotosDragging && photosRefCurrent) {
      photosRefCurrent.scrollLeft -= e.movementX
    }
  }

  return (
    <StyledPhotos
      className={`flex gap-2 overflow-auto h-[130px] ${className}`}
      onMouseMove={onMouseMovePhotos}
      onClick={(e) => e.preventDefault()}
      onMouseDown={() => setIsPhotosDragging(true)}
      onMouseUp={() => setIsPhotosDragging(false)}
      onMouseLeave={() => setIsPhotosDragging(false)}
      aria-label={ariaLabel}
      ref={photosRef}
    >
      {photos.map((photo, index) => {
        const isLockedFromSecond = isLocked && index > 0 ? isLocked : false
        const url = photo.blurred
          ? photo.url || 'https://image2.gnsister.com/images/img_photo_blurred.png'
          : photo.url
            ? getResizedImageUrl(photo.url, { height: 520 })
            : ''
        return (
          <StyledPhotoWrapper key={`${photo.url}-${index}`}>
            <StyledPhoto
              $isLocked={isLockedFromSecond}
              src={url}
              width={width}
              height={height}
              isRatioMaintained={false}
              alt={photo.alt}
              onClick={() => {
                onClickPhoto?.(index)
                useViewer &&
                  imageViewer.open(
                    photos.map((item) => item.url || ''),
                    index,
                  )
              }}
            />
            {isLockedFromSecond && <StyledLockIcon name={CheetahIconPack.LOCKED} />}
            {!!photo.title && (
              <div className="absolute bottom-0 left-0 w-6 h-6 bg-black/60 flex items-center justify-center">
                <Typography type="caption2" color="white">
                  {photo.title}
                </Typography>
              </div>
            )}
          </StyledPhotoWrapper>
        )
      })}
    </StyledPhotos>
  )
}

const StyledPhotos = styled.div`
  display: flex;
  overflow: auto hidden;
  user-select: none;
  & * {
    user-select: none;
    -webkit-user-drag: none;
  }
  *&::-webkit-scrollbar {
    width: 0px;
    background: transparent;
    display: none;
  }
`

const StyledPhotoWrapper = styled.div`
  position: relative;
  &:first-child {
    margin-left: 16px;
  }
`

const StyledPhoto = styled(SquareImage)<{ $isLocked: boolean }>`
  ${({ $isLocked }) =>
    $isLocked &&
    css`
      overflow: hidden;
      background-color: transparent;
      img {
        filter: blur(11px);
      }
    `}
  ${({ theme }) => theme.typography.caption2}
`

const StyledLockIcon = styled(CheetahIcon)`
  position: absolute;
  z-index: 1;
  left: calc(60px - 32px / 2);
  top: calc(60px - 32px / 2);
`

export default PreviewPhotoSlider
